import { useEffect, useState } from 'react';
import { ResizableTextArea } from '../resizableTextArea/ResizableTextArea';
import SelectFilteredModalTable from './SelectFilteredModalTable';

export interface RowHeading {
  rowIdx: number;
  heading: JSX.Element;
}

interface Props {
  data: any[];
  columns: any;
  onChange: (value: any) => void;
  onQueryChange?: (value: any) => void;
  onSelected?: (value: any) => void;
  labelKey: string;
  valueKey: string;
  value?: any;
  isReadOnly?: boolean;
  placeholder?: string;
  className?: string;
  displayValue?: string;
  resize?: boolean;
  isFormField?: boolean;
  label?: string;
  isFocused: boolean;
  aboveRowHeading?: RowHeading[];
  recalculate?: any;
  tabIndex?: number;
  isPrint?: boolean;
}

/**
 * Autocomplete component
 */
export default function SelectAutocompleteTable({
  data,
  columns,
  onChange,
  onQueryChange,
  onSelected,
  value,
  labelKey,
  valueKey,
  isReadOnly,
  placeholder,
  className,
  displayValue,
  resize = true,
  isFormField,
  label,
  isFocused,
  aboveRowHeading,
  recalculate,
  tabIndex,
  isPrint,
}: Props) {
  const [localData, setLocalData] = useState<any[]>(data);
  const [selected, setSelected] = useState<any>();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>(value ?? '');
  const [query, setQuery] = useState<string>('');
  const [isTyping, setIsTyping] = useState<boolean>(false);

  /**
   * Delay for 500ms before api call
   */
  useEffect(() => {
    if (query === '') return;
    setIsTyping(true);
    const typingTimeout = setTimeout(() => setIsTyping(false), 500);
    return () => {
      clearTimeout(typingTimeout);
    };
  }, [query]);

  /**
   * Reset query, close modal
   */
  const handleOnClose = () => {
    setLocalData([]);
    setQuery('');
    setIsOpen(false);
  };

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  /**
   * Filter items by query
   */
  const filteredItems =
    query && query === ''
      ? localData
      : localData.filter(
          (item) =>
            (item[labelKey] !== null &&
              item[labelKey] !== undefined &&
              item[labelKey]
                .toLowerCase()
                .replace(/\s+/g, '')
                .includes(query.toString().toLowerCase().replace(/\s+/g, ''))) ||
            (valueKey &&
              item[valueKey] !== null &&
              item[valueKey] !== undefined &&
              item[valueKey]
                .toLowerCase()
                .replace(/\s+/g, '')
                .includes(query.toString().toLowerCase().replace(/\s+/g, ''))),
        );

  useEffect(() => {
    if (
      !isFocused ||
      !localData ||
      localData.length === 0 ||
      !filteredItems ||
      filteredItems.length === 0 ||
      query === '' ||
      isTyping
    ) {
      return;
    }
    setIsOpen(true);
  }, [isTyping]);

  /**
   * Selects item, sets input value
   */
  const handleSelect = (opt: any) => {
    if (!isOpen) return;
    setInputValue(displayValue ? opt[displayValue] : opt[labelKey]);
    setSelected(opt);
    onSelected && onSelected(opt);
    handleOnClose();
  };

  useEffect(() => {
    if (inputValue === '' && selected && selected[labelKey] !== undefined) setSelected({});
    if (selected && selected[labelKey] === inputValue) {
      onChange(selected);
    }
  }, [selected, inputValue]);

  useEffect(() => {
    if (!value) return setInputValue('');
    const tmpInputVal = localData.find((x: any) => x[valueKey] === value);
    // item found in localData
    if (value && tmpInputVal) {
      setInputValue(value);
      handleSelect(localData[localData.findIndex((x: any) => x[valueKey] === value)]);
    }
    // item not found - show textBox
    if (value && !tmpInputVal) {
      setQuery(value);
      setInputValue(value);
    }
  }, [value]);

  /**
   * Call api, reset delay
   */
  useEffect(() => {
    if (!isFocused) return;
    onChange(inputValue.trim());
    if (
      isTyping ||
      query === '' ||
      !inputValue ||
      !onQueryChange ||
      (selected && selected[labelKey] === inputValue)
    )
      return;
    onQueryChange(inputValue.trim());
  }, [inputValue, isTyping, query]);

  return (
    <div className="w-full h-full">
      <div className="w-full h-auto">
        <ResizableTextArea
          isFormField={isFormField}
          label={label}
          className={`border border-gray-300 pl-2 w-full rounded-md appearance-none block placeholder-gray-500 text-gray-900 focus:outline-none resize-none overflow-hidden ${className}`}
          placeholder={placeholder}
          value={inputValue}
          resize={resize}
          onChange={(e: string) => {
            setInputValue(e);
            setQuery(e);
          }}
          recalculate={recalculate}
          isRequired={isReadOnly}
          modalOpen={filteredItems.length > 0 && isOpen && isFocused}
          tabIndex={tabIndex}
          isPrint={isPrint}
        />
        <SelectFilteredModalTable
          data={filteredItems ?? []}
          columns={columns}
          open={filteredItems.length > 0 && isOpen && isFocused}
          onClose={handleOnClose}
          labelKey="name"
          valueKey="id"
          showResult={false}
          onSelected={(val) => handleSelect(val)}
          onChange={(val) => handleSelect(val)}
          inputClassName={`border-0`}
          modalClassName={`left-[10%] right-[10%] mt-5`}
          aboveRowHeading={aboveRowHeading}
          useSelection
        />
      </div>
    </div>
  );
}
